import { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import { msgErro } from '../services/ui';
import { useSort } from '../helpers/useSort';
import { useModalSafeClose } from '../helpers/useModalSafeClose';
import { useAuth } from '../contexts/AuthContext';
import SeletorFornecedor from '../components/SeletorFornecedor';
import './Pedidos.css';

const brl = (n) => 'R$ ' + Number(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtQtd = (n) => Number(n || 0).toLocaleString('pt-BR', { maximumFractionDigits: 2 });
const un = (u) => (String(u || '').toUpperCase() === 'M2' ? 'm²' : String(u || 'un').toLowerCase());
// '2026-08-22' -> '22/08/26' (sem new Date, que desloca por fuso).
const fmtData = (iso) => { const [a, m, d] = String(iso || '').split('-'); return a ? `${d}/${m}/${a.slice(2)}` : '—'; };
const norm = (s) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const SITUACAO = {
  atrasado: { rotulo: 'Atrasado', badge: 'badge-negativo' },
  parcial: { rotulo: 'Parcial', badge: 'badge-pendente' },
  aberto: { rotulo: 'Em aberto', badge: 'badge-neutro' },
  entregue: { rotulo: 'Entregue', badge: 'badge-positivo' },
  cancelado: { rotulo: 'Cancelado', badge: 'badge-neutro' },
};
const Situacao = ({ s, dias }) => (
  <span className={`badge ${SITUACAO[s]?.badge || 'badge-neutro'}`} title={s === 'atrasado' ? `Previsão de chegada passou há ${dias} dia${dias === 1 ? '' : 's'}` : undefined}>
    {SITUACAO[s]?.rotulo || s}{s === 'atrasado' && dias > 0 ? ` · ${dias}d` : ''}
  </span>
);

// Pedidos de compra que o Centrão fez pro fornecedor e ainda não chegaram por inteiro.
export default function Pedidos() {
  const { user } = useAuth();
  const multiCnpj = (user?.fornecedores || []).length > 1;
  const [fornSel, setFornSel] = useState('');
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');
  const [filtro, setFiltro] = useState('todos'); // todos | atrasado | parcial | proximos
  const [busca, setBusca] = useState('');
  const [aberto, setAberto] = useState(null); // pedido no modal
  const sort = useSort('previsao', 'asc');
  const modalSafe = useModalSafeClose(() => setAberto(null));

  useEffect(() => {
    setDados(null); setErro('');
    api.get('/pedidos', { params: fornSel ? { fornecedor: fornSel } : {} })
      .then((r) => setDados(r.data))
      .catch((e) => setErro(msgErro(e, 'Erro ao carregar os pedidos.')));
  }, [fornSel]);

  useEffect(() => {
    if (!aberto) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setAberto(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [aberto]);

  const lista = useMemo(() => {
    if (!dados) return [];
    const hoje = dados.hoje;
    const em7 = new Date(Date.parse(hoje) + 7 * 86400000).toISOString().slice(0, 10);
    const t = norm(busca.trim());
    return dados.pedidos.filter((p) => {
      if (filtro === 'atrasado' && !p.atrasado) return false;
      if (filtro === 'parcial' && p.situacao !== 'parcial') return false;
      if (filtro === 'proximos' && (p.atrasado || !p.previsao || p.previsao < hoje || p.previsao > em7)) return false;
      if (!t) return true;
      return String(p.numped).includes(t)
        || p.itens.some((i) => norm(i.descr).includes(t) || i.codpro.includes(t) || norm(i.codFabricante).includes(t));
    });
  }, [dados, filtro, busca]);

  const ordenados = sort.aplicar(lista, (p, k) => (k === 'previsao' ? (p.previsao || '9999') : k === 'loja' ? p.filialNome : p[k]));
  const r = dados?.resumo;
  // Card do topo. Com `chave`, vira atalho de filtro (clicar de novo volta pra todos).
  const card = (chave, rotulo, valor, sub, cls = '') => {
    const corpo = (<><div className="stat-label">{rotulo}</div><div className="stat-value">{valor}</div><div className="pd-card-sub">{sub}</div></>);
    return chave ? (
      <button type="button" className={`card-stat pd-card clicavel ${cls} ${filtro === chave ? 'ativo' : ''}`}
        onClick={() => setFiltro(filtro === chave ? 'todos' : chave)} title="Clique para filtrar a lista">{corpo}</button>
    ) : <div className={`card-stat pd-card ${cls}`}>{corpo}</div>;
  };

  return (
    <div>
      <h4 className="fw-bold mb-1"><i className="bi bi-truck me-2" style={{ color: 'var(--primary)' }} />Pedidos</h4>
      <p className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>
        Pedidos de compra que o Centrão fez para você e ainda não chegaram por inteiro.
      </p>

      <div className="pd-bar"><SeletorFornecedor value={fornSel} onChange={setFornSel} /></div>

      {erro && <div className="alert alert-danger">{erro}</div>}
      {!dados && !erro && <div className="text-center text-muted py-5"><div className="spinner-border spinner-border-sm me-2" />Carregando pedidos...</div>}

      {dados && (
        <>
          <div className="pd-cards">
            {card(null, 'Pedidos em aberto', r.pedidos, `${r.itensAbertos} ite${r.itensAbertos === 1 ? 'm' : 'ns'} a entregar`)}
            {card(null, 'Valor em aberto', brl(r.valorAberto), 'o que ainda falta entregar', 'valor')}
            {card('atrasado', 'Atrasados', r.atrasados, `${brl(r.valorAtrasado)} com previsão vencida`, 'alerta')}
            {card('proximos', 'Chegam em 7 dias', r.proximos7, brl(r.valorProximos7), 'proximo')}
          </div>

          <div className="pd-filtros">
            <div className="pd-seg">
              {[['todos', 'Todos'], ['atrasado', 'Atrasados'], ['parcial', 'Parciais'], ['proximos', 'Próximos 7 dias']].map(([k, rot]) => (
                <button key={k} type="button" className={filtro === k ? 'ativo' : ''} onClick={() => setFiltro(k)}>{rot}</button>
              ))}
            </div>
            <div className="pd-busca">
              <i className="bi bi-search" />
              <input type="text" placeholder="Nº do pedido, produto ou código..." value={busca} onChange={(e) => setBusca(e.target.value)} />
            </div>
          </div>

          {ordenados.length === 0 ? (
            <div className="pd-vazio">
              <i className="bi bi-inbox" />
              {dados.pedidos.length === 0
                ? <>Nenhum pedido de compra em aberto para {multiCnpj && fornSel ? 'este CNPJ' : 'você'}.<small>Aqui aparecem os pedidos feitos para o seu CNPJ. Produto seu comprado por meio de distribuidor é pedido do distribuidor.</small></>
                : 'Nenhum pedido com esse filtro.'}
            </div>
          ) : (
            <div className="table-responsive pd-tab-wrap">
              <table className="table table-hover table-for pd-tab">
                <thead>
                  <tr>
                    <th onClick={() => sort.toggle('numped')}>Pedido {sort.icon('numped')}</th>
                    <th onClick={() => sort.toggle('dtpedido')}>Data {sort.icon('dtpedido')}</th>
                    <th onClick={() => sort.toggle('loja')}>Entregar em {sort.icon('loja')}</th>
                    {multiCnpj && <th>CNPJ do pedido</th>}
                    <th onClick={() => sort.toggle('comprador')}>Comprador {sort.icon('comprador')}</th>
                    <th className="num" onClick={() => sort.toggle('itensAbertos')}>Itens {sort.icon('itensAbertos')}</th>
                    <th className="num" onClick={() => sort.toggle('valorAberto')}>Em aberto {sort.icon('valorAberto')}</th>
                    <th className="num" onClick={() => sort.toggle('entreguePct')}>Entregue {sort.icon('entreguePct')}</th>
                    <th onClick={() => sort.toggle('previsao')}>Previsão {sort.icon('previsao')}</th>
                    <th onClick={() => sort.toggle('diasAtraso')}>Situação {sort.icon('diasAtraso')}</th>
                  </tr>
                </thead>
                <tbody>
                  {ordenados.map((p) => (
                    <tr key={p.numped} className={p.atrasado ? 'atrasado' : ''} onClick={() => setAberto(p)} role="button">
                      <td className="pd-num">{p.numped}</td>
                      <td>{fmtData(p.dtpedido)}</td>
                      <td>{p.filialNome}<small className="pd-cnpj">{p.filialCnpj}</small></td>
                      {multiCnpj && <td><small className="pd-cnpj sozinho">{p.fornecedor.cnpj}</small></td>}
                      <td>{p.comprador || '—'}</td>
                      <td className="num">{p.itensAbertos}<span className="pd-de">/{p.itensTotal}</span></td>
                      <td className="num fw-semibold">{brl(p.valorAberto)}</td>
                      <td className="num">
                        <div className="pd-barra" title={`${p.entreguePct}% do valor já entregue`}><span style={{ width: `${Math.min(p.entreguePct, 100)}%` }} /></div>
                        <small>{p.entreguePct.toLocaleString('pt-BR')}%</small>
                      </td>
                      <td>{fmtData(p.previsao)}</td>
                      <td><Situacao s={p.situacao} dias={p.diasAtraso} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="pd-nota">
            <i className="bi bi-info-circle me-1" />
            <b>Atrasado</b> = a previsão de chegada registrada no pedido já passou. <b>Entregue</b> é calculado pelo valor
            (itens cancelados não contam). Clique no pedido para ver os itens e as notas já recebidas.
          </p>
        </>
      )}

      {aberto && <ModalPedido p={aberto} safe={modalSafe} onFechar={() => setAberto(null)} />}
    </div>
  );
}

function ModalPedido({ p, safe, onFechar }) {
  const temCancelado = p.itens.some((i) => i.cancelado > 0);
  return (
    <div className="pd-overlay" {...safe.overlayProps}>
      <div className="pd-modal">
        <div className="pd-modal-head">
          <div>
            <div className="pd-modal-titulo">Pedido {p.numped} <Situacao s={p.situacao} dias={p.diasAtraso} /></div>
            <div className="pd-modal-sub">
              Feito em {fmtData(p.dtpedido)} · comprador <b>{p.comprador || '—'}</b>
            </div>
          </div>
          <button className="pd-x" onClick={onFechar} aria-label="Fechar"><i className="bi bi-x-lg" /></button>
        </div>

        <div className="pd-modal-body">
          <div className="pd-ficha">
            <div><span>Entregar em</span><b>{p.filialNome}</b><small>CNPJ {p.filialCnpj || '—'}</small></div>
            <div><span>Pedido para</span><b>{p.fornecedor.nome}</b><small>CNPJ {p.fornecedor.cnpj || '—'}</small></div>
            <div><span>Valor do pedido</span><b>{brl(p.valor)}</b><small>{brl(p.valorAberto)} em aberto · {p.entreguePct.toLocaleString('pt-BR')}% entregue</small></div>
          </div>

          <div className="table-responsive">
            <table className="table table-for pd-itens">
              <thead>
                <tr>
                  <th>Cód. fabricante</th><th>Produto</th>
                  <th className="num">Pedido</th><th className="num">Recebido</th>
                  {temCancelado && <th className="num">Cancelado</th>}
                  <th className="num">Falta</th><th className="num">Preço</th><th className="num">Falta (R$)</th>
                  <th>Previsão</th><th>Situação</th>
                </tr>
              </thead>
              <tbody>
                {p.itens.map((i) => (
                  <tr key={i.codpro} className={i.falta > 0 ? '' : 'feito'}>
                    <td data-label="Cód. fabricante">{i.codFabricante || '—'}</td>
                    <td data-label="Produto" className="pd-prod">{i.descr}<small>cód. {i.codpro}</small></td>
                    <td data-label="Pedido" className="num">{fmtQtd(i.pedido)} {un(i.unid)}</td>
                    <td data-label="Recebido" className="num">{i.recebido > 0 ? `${fmtQtd(i.recebido)} ${un(i.unid)}` : '—'}</td>
                    {temCancelado && <td data-label="Cancelado" className="num">{i.cancelado > 0 ? `${fmtQtd(i.cancelado)} ${un(i.unid)}` : '—'}</td>}
                    <td data-label="Falta" className="num fw-semibold">{i.falta > 0 ? `${fmtQtd(i.falta)} ${un(i.unid)}` : '—'}</td>
                    <td data-label="Preço" className="num">{brl(i.preco)}</td>
                    <td data-label="Falta (R$)" className="num">{i.valorFalta > 0 ? brl(i.valorFalta) : '—'}</td>
                    <td data-label="Previsão">{i.falta > 0 ? fmtData(i.previsao) : '—'}</td>
                    <td data-label="Situação"><Situacao s={i.situacao} dias={i.diasAtraso} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pd-notas">
            <div className="pd-notas-titulo"><i className="bi bi-receipt me-1" />Notas que já atenderam este pedido</div>
            {p.notas.length === 0
              ? <div className="text-muted small">Nenhuma nota recebida ainda.</div>
              : p.notas.map((n) => (
                <span key={`${n.nota}-${n.serie}`} className="pd-nf">
                  NF {n.nota}{n.serie ? `/${n.serie}` : ''} · chegou {fmtData(n.chegada)} · {n.itens} ite{n.itens === 1 ? 'm' : 'ns'}
                </span>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
